import useOnChainBalance, { type UseOnChainBalanceOptions } from './use-onchain-balance';
import usePolyValue, { type UsePolyValueOptions } from './use-poly-value';

export interface UsePolyPortfolioOptions
  extends Pick<UsePolyValueOptions, 'user' | 'wallets' | 'enabled'> {
  includeDetails?: UseOnChainBalanceOptions['includeDetails'];
  polyValueSWR?: UsePolyValueOptions['swr'];
  onChainBalanceSWR?: UseOnChainBalanceOptions['swr'];
}

export function usePolyPortfolio(options: UsePolyPortfolioOptions = {}) {
  const {
    user,
    wallets,
    enabled = true,
    includeDetails,
    polyValueSWR,
    onChainBalanceSWR,
  } = options;
  const normalizedWallets = normalizeWallets(user ? [user] : (wallets ?? []));

  const polyValue = usePolyValue({
    user,
    wallets,
    enabled,
    swr: polyValueSWR,
  });
  const onChainBalance = useOnChainBalance({
    wallet: user,
    wallets,
    enabled,
    includeDetails,
    swr: onChainBalanceSWR,
  });

  const positionValue = polyValue.value;
  const cashValue = onChainBalance.totalBalance;
  const totalValue = positionValue + cashValue;
  const totalValueByWallet = Object.fromEntries(
    normalizedWallets.map((wallet) => [
      wallet,
      (polyValue.detail[wallet] ?? 0) + (onChainBalance.balanceByWallet[wallet] ?? 0),
    ]),
  );

  return {
    data: {
      totalValue,
      positionValue,
      cashValue,
      totalValueByWallet,
      positionDetail: polyValue.detail,
      balanceByWallet: onChainBalance.balanceByWallet,
      detailsByWallet: onChainBalance.detailsByWallet,
      wallets: onChainBalance.wallets,
    },
    totalValue,
    positionValue,
    cashValue,
    totalValueByWallet,
    positionDetail: polyValue.detail,
    balanceByWallet: onChainBalance.balanceByWallet,
    detailsByWallet: onChainBalance.detailsByWallet,
    wallets: onChainBalance.wallets,
    error: polyValue.error ?? onChainBalance.error,
    isLoading: polyValue.isLoading || onChainBalance.isLoading,
    isValidating: polyValue.isValidating || onChainBalance.isValidating,
    refresh: () => Promise.all([polyValue.refresh(), onChainBalance.refresh()]),
  };
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))];
}

export default usePolyPortfolio;
